import type { HttpPort } from "@/core/contracts/services";
import type {
    FixResult,
    IoRowAction,
    IoRun,
    IoRunRow,
    MappingResult,
    TemplateBrief,
} from "./types";

// Thin typed wrapper over the shipped `/api/io/**` routes. Built from the
// runtime's shared HttpPort, so it inherits bearer-JWT auth, token refresh,
// and tenant-header resolution like every other service. The engine is
// headless — this is the *form-UI* caller; the assistant is a separate caller
// of the same backend (see the module's AI-reuse note).

interface SearchEnvelope<T> {
    records?: T[];
    count?: number;
}

function qs(params: Record<string, string | number | undefined | null>): string {
    const parts: string[] = [];
    for (const [key, value] of Object.entries(params)) {
        if (value === undefined || value === null || value === "") continue;
        parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
    }
    return parts.length ? `?${parts.join("&")}` : "";
}

export class IoService {
    constructor(private readonly http: HttpPort) {}

    // ── Templates ────────────────────────────────────────────────────────────

    async templatesForModel(modelKey: string): Promise<TemplateBrief[]> {
        const res = await this.http.get<SearchEnvelope<TemplateBrief>>(
            `/api/io/templates${qs({ target_model_id: modelKey })}`,
        );
        return res.records ?? [];
    }

    /** Column definitions for a template (ordered by sort_order) — used to
     *  generate the blank "download template" file. */
    async templateColumns(
        code: string,
    ): Promise<Array<{ header_label?: string; field_name?: string; required?: boolean }>> {
        const res = await this.http.get<{
            columns?: Array<{ header_label?: string; field_name?: string; required?: boolean }>;
        }>(`/api/io/templates/${encodeURIComponent(code)}`);
        return res.columns ?? [];
    }

    // ── Upload → preview ──────────────────────────────────────────────────────

    /** Upload raw file bytes; returns the created run UUID (status=preview).
     *  `columnOverrides` (field_name → source header) applies an AI/manual
     *  column mapping when the file's headers don't match the template. */
    async upload(args: {
        templateCode: string;
        file: File;
        parentUuid?: string;
        columnOverrides?: Record<string, string>;
    }): Promise<{ success: boolean; run_uuid?: string; error?: string }> {
        const bytes = await args.file.arrayBuffer();
        const hasOverrides = args.columnOverrides && Object.keys(args.columnOverrides).length > 0;
        const query = qs({
            template_code: args.templateCode,
            filename: args.file.name,
            content_type: args.file.type || "application/octet-stream",
            parent_record_uuid: args.parentUuid,
            column_overrides: hasOverrides ? JSON.stringify(args.columnOverrides) : undefined,
        });
        return this.http.postBytes<{ success: boolean; run_uuid?: string; error?: string }>(
            `/api/io/runs${query}`,
            bytes,
            args.file.type || "application/octet-stream",
        );
    }

    /** Apply corrected field values to one row and re-validate it.
     *  `fieldValues` is keyed by target field_name (e.g. {currency: "EUR"}). */
    async revalidateRow(
        runUuid: string,
        rowNumber: number,
        fieldValues: Record<string, string>,
    ): Promise<{ success: boolean; result?: { action: IoRowAction; row_count_valid: number; row_count_invalid: number }; error?: string }> {
        return this.http.post(`/api/io/runs/${runUuid}/rows/${rowNumber}/revalidate`, {
            field_values: fieldValues,
        });
    }

    // ── Run inspection ────────────────────────────────────────────────────────

    async run(runUuid: string): Promise<IoRun> {
        const res = await this.http.get<{ record: IoRun }>(`/api/io/runs/${runUuid}`);
        return res.record;
    }

    async rows(
        runUuid: string,
        opts: { offset?: number; limit?: number; action?: string } = {},
    ): Promise<{ records: IoRunRow[]; count: number }> {
        const res = await this.http.get<SearchEnvelope<IoRunRow>>(
            `/api/io/runs/${runUuid}/rows${qs({
                offset: opts.offset ?? 0,
                limit: opts.limit ?? 100,
                action: opts.action,
            })}`,
        );
        return { records: res.records ?? [], count: res.count ?? 0 };
    }

    // ── Lifecycle ─────────────────────────────────────────────────────────────

    async commit(runUuid: string, forcePartial = false): Promise<{ success: boolean; result?: IoRun; error?: string }> {
        return this.http.post(`/api/io/runs/${runUuid}/commit`, { force_partial: forcePartial });
    }

    async cancel(runUuid: string): Promise<{ success: boolean; result?: IoRun; error?: string }> {
        return this.http.post(`/api/io/runs/${runUuid}/cancel`, {});
    }

    errorReportUrl(runUuid: string): string {
        return `/api/io/runs/${runUuid}/error-report`;
    }

    /** Download the error-report CSV via a bearer-authenticated fetch (the
     *  route is auth-gated, so a plain <a href> would 401), then trigger a
     *  browser save. */
    async downloadErrorReport(runUuid: string, filename = "import-errors.csv"): Promise<void> {
        const blob = await this.http.getBlob(this.errorReportUrl(runUuid));
        const url = URL.createObjectURL(blob);
        try {
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
        } finally {
            URL.revokeObjectURL(url);
        }
    }

    // ── AI proposers (read-only; degrade to status="disabled") ────────────────

    async suggestMapping(templateCode: string, fileHeaders: string[]): Promise<MappingResult> {
        const res = await this.http.post<{ success: boolean } & MappingResult>(
            `/api/io/ai/suggest-mapping`,
            { template_code: templateCode, file_headers: fileHeaders },
        );
        return { status: res.status, mappings: res.mappings ?? [] };
    }

    async suggestFix(args: {
        field: string;
        columnLabel: string;
        badValue: string;
        errorCode: string;
        reason: string;
        dataType?: string;
    }): Promise<FixResult> {
        const res = await this.http.post<{ success: boolean } & FixResult>(`/api/io/ai/suggest-fix`, {
            field: args.field,
            column_label: args.columnLabel,
            bad_value: args.badValue,
            error_code: args.errorCode,
            reason: args.reason,
            data_type: args.dataType ?? "",
        });
        return { status: res.status, value: res.value ?? null, note: res.note ?? "", confidence: res.confidence ?? 0 };
    }
}
